import React, { useState } from 'react';
import { Box, Paper, Typography, useMediaQuery } from '@mui/material';

const toneStyles = {
  amber: {
    borderColor: '#f5c26b',
    background: 'linear-gradient(135deg, rgba(255, 233, 194, 0.85), rgba(255, 212, 137, 0.75))',
    color: '#8a5200',
  },
  green: {
    borderColor: '#7fd3a1',
    background: 'linear-gradient(135deg, rgba(206, 244, 224, 0.9), rgba(167, 227, 193, 0.7))',
    color: '#175c3a',
  },
  blue: {
    borderColor: '#96b9f7',
    background: 'linear-gradient(135deg, rgba(220, 234, 255, 0.9), rgba(186, 209, 255, 0.7))',
    color: '#22447a',
  },
  red: {
    borderColor: '#f2a2a1',
    background: 'linear-gradient(135deg, rgba(255, 221, 221, 0.95), rgba(245, 179, 179, 0.75))',
    color: '#7a1d1a',
  },
  slate: {
    borderColor: 'rgba(98, 115, 136, 0.25)',
    background: 'rgba(243, 245, 248, 0.8)',
    color: '#2f3a46',
  },
};

export const DetailPaneStatusCard = ({ label, value, tone, type }) => {
  const style = toneStyles[tone] || toneStyles.slate;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor: 'rgba(15, 24, 35, 0.08)',
        background: 'rgba(255, 255, 255, 0.7)',
        boxShadow: '0 12px 30px rgba(20, 32, 60, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minHeight: 100,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          fontWeight: 700,
          color: 'rgba(15, 24, 35, 0.55)',
        }}
      >
        {label}
      </Typography>
      {type === 'pill' ? (
        <Box
          sx={{
            alignSelf: 'flex-start',
            px: 1.5,
            py: 0.6,
            borderRadius: 999,
            border: '1px solid',
            borderColor: style.borderColor,
            background: style.background,
            color: style.color,
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {value}
        </Box>
      ) : (
        <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 15 }}>{value}</Typography>
      )}
    </Paper>
  );
};

export const DetailPaneStatusFlipCard = ({
  label,
  statusLabel,
  dateLabel,
  dateValue,
  tone,
}) => {
  const style = toneStyles[tone] || toneStyles.slate;
  const supportsHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const [isFlipped, setIsFlipped] = useState(false);

  const handleToggle = () => {
    if (!supportsHover) {
      setIsFlipped((prev) => !prev);
    }
  };

  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      aria-pressed={isFlipped}
      data-flipped={isFlipped ? 'true' : 'false'}
      onClick={handleToggle}
      onKeyDown={(event) => {
        if (!supportsHover && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          setIsFlipped((prev) => !prev);
        }
      }}
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor: 'rgba(15, 24, 35, 0.08)',
        background: 'rgba(255, 255, 255, 0.7)',
        boxShadow: '0 12px 30px rgba(20, 32, 60, 0.08)',
        minHeight: 100,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        perspective: '1200px',
        overflow: 'hidden',
        cursor: supportsHover ? 'default' : 'pointer',
        outline: 'none',
        '&:focus-visible': {
          boxShadow: '0 0 0 3px rgba(74, 99, 255, 0.25)',
        },
        '& .flip-inner': {
          position: 'relative',
          flex: 1,
          minHeight: 0,
          width: '100%',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.55s ease',
        },
        '& .flip-front': {
          position: 'absolute',
          inset: 0,
          background: 'transparent',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: 1,
          textAlign: 'left',
          backfaceVisibility: 'hidden',
        },
        '& .flip-back': {
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: 1,
          textAlign: 'left',
          backfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)',
          zIndex: 0,
        },
        '&[data-flipped="true"] .flip-inner': {
          transform: 'rotateY(180deg)',
        },
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover .flip-inner': {
            transform: 'rotateY(180deg)',
          },
        },
      }}
    >
      <Box className="flip-inner">
        <Box className="flip-front">
          <Typography
            variant="caption"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              fontWeight: 700,
              color: 'rgba(15, 24, 35, 0.55)',
            }}
          >
            {label}
          </Typography>
          <Box
            sx={{
              alignSelf: 'flex-start',
              px: 1.5,
              py: 0.6,
              borderRadius: 999,
              border: '1px solid',
              borderColor: style.borderColor,
              background: style.background,
              color: style.color,
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {statusLabel}
          </Box>
          {null}
        </Box>
        <Box className="flip-back">
          <Typography
            variant="caption"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              fontWeight: 700,
              color: 'rgba(15, 24, 35, 0.55)',
            }}
          >
            {dateLabel}
          </Typography>
          <Box
            sx={{
              alignSelf: 'flex-start',
              px: 1.5,
              py: 0.6,
              borderRadius: 999,
              border: '1px solid',
              borderColor: style.borderColor,
              background: style.background,
              color: style.color,
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {dateValue}
          </Box>
          {null}
        </Box>
      </Box>
    </Paper>
  );
};
